import { HTTP_HYPER_ESPACE_BACKEND } from '@/services/http-common'

export const uploadVideoMixin = {
  data () {
    return {
      uploadProgress: 0,
      isUploading: false,
      uploadAttempt: 1,
      uploadRetryLimit: 3
    }
  },
  computed: {
    isEditorialContext () {
      return this.$route.path.includes('editorial')
    }
  },
  methods: {
    /**
         * Upload une vidéo avec gestion des chunks et retry automatique
         * @param {Array} newVideo - Tableau contenant le fichier vidéo à uploader
         * @param {Object} options - Options d'upload (optionnel)
         * @param {Function} options.onProgress - Callback pour suivre le progrès
         * @param {Function} options.onChunkUploaded - Callback appelé après chaque chunk uploadé
         * @param {Number} options.retryLimit - Nombre maximum de tentatives (défaut: 3)
         * @returns {Promise<Object>} Résultat de l'upload
         */
    async uploadVideo (newVideo, options = {}) {
      if (!newVideo || newVideo.length === 0) {
        return { success: false, error: 'Aucun fichier vidéo fourni' }
      }

      const video = newVideo[0]
      const {
        onProgress = null,
        onChunkUploaded = null,
        retryLimit = this.uploadRetryLimit
      } = options

      this.isUploading = true
      this.uploadProgress = 0

      // console.log('Début de l\'upload:', video.name)
      // console.log('URL de l\'API configurée:', HTTP_HYPER_ESPACE_BACKEND.defaults.baseURL)

      try {
        // Déterminer la taille des chunks selon la taille du fichier
        let chunkSize = 1024 * 1024 // 1 MO par défaut
        if (video.size > 500 * 1024 * 1024) { // > 500 Mo
          chunkSize = 20 * 1024 * 1024 // 7 Mo
        }

        // console.log('Taille des chunks:', chunkSize)

        const uploadId = video.id || this.generateUploadId()
        const totalChunks = Math.ceil(video.size / chunkSize)
        let finalResponse = null

        // Upload séquentiel des chunks avec retry individualisé
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const chunkResponse = await this.uploadChunkWithRetry(
            video,
            chunkIndex,
            chunkSize,
            uploadId,
            totalChunks,
            retryLimit,
            onProgress,
            onChunkUploaded
          )

          // Sauvegarder la réponse finale (du dernier chunk)
          if (chunkIndex === totalChunks - 1) {
            finalResponse = chunkResponse
          }
        }

        // console.log('Upload terminé avec succès!')
        this.uploadProgress = 100

        return {
          success: true,
          message: 'Upload terminé avec succès!',
          uploadId: uploadId,
          videoData: finalResponse ? finalResponse.data : null
        }
      } catch (error) {
        // console.error('Upload échoué:', error.message)
        return {
          success: false,
          error: error.message,
          uploadId: video.id || null
        }
      } finally {
        this.isUploading = false
      }
    },

    /**
         * Upload un chunk avec retry automatique
         * @param {File} video - Fichier vidéo
         * @param {Number} chunkIndex - Index du chunk
         * @param {Number} chunkSize - Taille du chunk
         * @param {String} uploadId - ID unique de l'upload
         * @param {Number} totalChunks - Nombre total de chunks
         * @param {Number} retryLimit - Limite de retry
         * @param {Function} onProgress - Callback de progrès
         * @param {Function} onChunkUploaded - Callback après upload chunk
         * @param {Number} attempt - Numéro de tentative (par défaut: 1)
         * @returns {Promise<Object>} Réponse de l'upload
         */
    async uploadChunkWithRetry (video, chunkIndex, chunkSize, uploadId, totalChunks, retryLimit, onProgress, onChunkUploaded, attempt = 1) {
      try {
        const start = chunkIndex * chunkSize
        const end = Math.min(video.size, start + chunkSize)
        const chunk = video.file ? video.file.slice(start, end) : video.slice(start, end)

        const formData = new FormData()
        formData.append('video', chunk)
        formData.append('uploadId', uploadId)
        formData.append('chunkIndex', chunkIndex)
        formData.append('totalChunks', totalChunks)
        formData.append('originalFilename', video.name)

        // console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks} (attempt ${attempt})`)

        const response = await HTTP_HYPER_ESPACE_BACKEND.post('/upload/videos', formData, {
          timeout: 30000, // 30 secondes timeout
          headers: {
            'Content-Type': undefined // Laisser axios définir automatiquement le Content-Type pour FormData
          },
          onUploadProgress: (progressEvent) => {
            const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100
            const totalProgress = Math.floor(((chunkIndex + chunkProgress / 100) / totalChunks) * 100)

            this.uploadProgress = totalProgress

            if (onProgress) {
              onProgress(totalProgress, chunkIndex, totalChunks)
            }
            // console.log(`Progress: ${totalProgress}%`)
          }
        })

        // Callback après upload successful d'un chunk
        if (onChunkUploaded) {
          onChunkUploaded(chunkIndex, totalChunks, response)
        }
        // console.log(response)
        return response
      } catch (error) {
        // console.error(`Erreur chunk ${chunkIndex + 1}, tentative ${attempt}:`, error.message)

        // Diagnostic simple
        if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
          console.error('DIAGNOSTIC: Erreur réseau détectée')
          console.error('URL tentée:', HTTP_HYPER_ESPACE_BACKEND.defaults.baseURL + '/upload/videos')
          console.error('Vérifiez que le serveur API est démarré et accessible')
        }

        if (error.response) {
          console.error('Réponse serveur:', error.response.status, error.response.data)
        } else if (error.request) {
          console.error('Pas de réponse du serveur. Vérifiez la connectivité.')
        }

        // Si on n'a pas atteint la limite de retry
        if (attempt < retryLimit) {
          // Délai progressif : 500ms * tentative^2
          const delay = 500 * Math.pow(attempt, 2)
          // console.log(`Retry dans ${delay}ms...`)

          await new Promise(resolve => setTimeout(resolve, delay))
          return this.uploadChunkWithRetry(video, chunkIndex, chunkSize, uploadId, totalChunks, retryLimit, onProgress, onChunkUploaded, attempt + 1)
        } else {
          // Toutes les tentatives ont échoué
          const errorMsg = error.code === 'NETWORK_ERROR' || error.message === 'Network Error'
            ? `Impossible de joindre le serveur API (${HTTP_HYPER_ESPACE_BACKEND.defaults.baseURL}). Vérifiez que le serveur est démarré.`
            : error.message
          throw new Error(`Upload du chunk ${chunkIndex + 1} échoué après ${retryLimit} tentatives: ${errorMsg}`)
        }
      }
    },

    /**
         * Génère un ID unique pour l'upload
         * @returns {String} ID unique
         */
    generateUploadId () {
      return Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    },

    /**
         * Formate la taille d'un fichier en format lisible
         * @param {Number} bytes - Taille en bytes
         * @returns {String} Taille formatée
         */
    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    /**
         * Valide le fichier vidéo avant upload
         * @param {File} file - Fichier à valider
         * @param {Object} options - Options de validation
         * @returns {Object} Résultat de la validation
         */
    validateVideoFile (file, options = {}) {
      const {
        maxSize = 500 * 1024 * 1024, // 500 MB par défaut
        allowedTypes = [
          'video/mp4',
          'video/avi',
          'video/mov',
          'video/quicktime',
          'video/x-quicktime',
          'video/wmv',
          'video/flv',
          'video/webm'
        ]
      } = options

      if (!file) {
        return { valid: false, error: 'Aucun fichier fourni' }
      }

      if (file.size > maxSize) {
        return {
          valid: false,
          error: `La taille du fichier (${this.formatFileSize(file.size)}) dépasse la limite autorisée (${this.formatFileSize(maxSize)})`
        }
      }

      // Log pour diagnostic du type MIME
      // console.log('Fichier détecté:', file.name, 'Type MIME:', file.type)

      // Validation par extension de fichier (fallback si le type MIME n'est pas reconnu)
      const fileExtension = file.name.split('.').pop().toLowerCase()
      const allowedExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']

      const isValidMimeType = allowedTypes.includes(file.type)
      const isValidExtension = allowedExtensions.includes(fileExtension)

      // Accepter si le type MIME OU l'extension est valide
      if (!isValidMimeType && !isValidExtension) {
        return {
          valid: false,
          error: `Type de fichier non autorisé (${file.type}, .${fileExtension}). Types acceptés: ${allowedTypes.join(', ')} ou extensions: .${allowedExtensions.join(', .')}`
        }
      }

      // Log pour confirmation
      if (isValidExtension && !isValidMimeType) {
        console.log(`Fichier accepté grâce à son extension .${fileExtension} (type MIME non reconnu: ${file.type})`)
      }

      return { valid: true }
    }
  }
}
